import {
  CreateComponentArgs,
  CreateComponentUnion,
  GetDefaults,
} from '@common/components/ComponentBase';
import { CommonRegistry } from '@common/components/components';
import { EventNameToType } from '@common/events';
import { CommonServiceContainer } from '@common/services/CommonServiceContainer';
import { MessageQueueService } from '@common/services/MessageQueueService';
import { World } from '@common/World';

import { EntityStatePayload } from '../api';
import { buildEntitySnapshot } from '../utils/EntityUtils';

/**
 * Minimal interface for any ECS system.
 * Systems are run each tick. ECS mutations auto-generate payloads via World tracking.
 * Custom payloads (dialogue, time sync, etc.) are pushed to MessageQueueService.
 */
export interface System {
  run(deltaTime: number): void;
}

type RegistryConstraint = CommonRegistry & { readonly __defaults: object };

/**
 * Base class for all ECS systems.
 *
 * Systems receive services via constructor injection through the CommonServiceContainer.
 * All systems extending this base class must accept services in their constructor.
 *
 * Key patterns:
 * - run() returns void (systems don't return payloads)
 * - ECS mutations (addComponent, updateComponent, etc.) auto-generate payloads via World
 * - Custom payloads use this.messageQueue.push()
 */
export abstract class BaseSystem<
  TWorld extends World<TRegistry>,
  TRegistry extends CommonRegistry,
> {
  protected services: CommonServiceContainer;

  /**
   * Probability (0-1) that this system runs on each tick.
   * 1 = always run (default), 0.1 = 10% chance to run.
   */
  protected probability: number = 1;

  constructor(services: CommonServiceContainer) {
    this.services = services;
  }

  /**
   * Check if this system should run based on probability.
   * Called before processing; returns false to skip the entire run.
   */
  protected shouldRun(): boolean {
    return this.probability >= 1 || Math.random() < this.probability;
  }

  /**
   * Run the system for one tick.
   * ECS mutations auto-generate payloads. Use messageQueue for custom payloads.
   * @param deltaTime Time elapsed since last tick in seconds
   */
  abstract run(deltaTime: number): void;

  /**
   * Get the world instance. Override in subclasses for typed access.
   */
  protected getWorld(): TWorld {
    return this.services.world.getWorld() as unknown as TWorld;
  }

  /**
   * Get the event bus service.
   */
  protected getEventBus() {
    return this.services.eventBus;
  }

  /**
   * Get the message queue for pushing custom (non-ECS) payloads.
   */
  protected get messageQueue(): MessageQueueService {
    return this.services.messageQueue;
  }

  /**
   * Update a component. The World auto-generates an EntityDeltaPayload.
   */
  updateComponent<K extends keyof TRegistry>(
    entityId: number,
    component: { readonly typeName: K } & TRegistry[K],
    args: Partial<TRegistry[K]>,
  ): void {
    this.getWorld().updateComponent(entityId, component, args);
  }

  /**
   * Add a component to an entity. The World auto-generates an EntityDeltaPayload.
   */
  addComponent<K extends keyof TRegistry>(
    entityId: number,
    component: { readonly typeName: K } & TRegistry[K],
  ): void {
    this.getWorld().addComponent(entityId, component);
  }

  /**
   * Remove a component from an entity. The World auto-generates an EntityDeltaPayload.
   */
  removeComponent(entityId: number, componentName: keyof TRegistry): void {
    this.getWorld().removeComponent(entityId, componentName);
  }

  /**
   * Create a new entity. The World auto-generates an EntityStatePayload.
   * @returns The new entity ID
   */
  createEntity(
    components: Extract<CreateComponentUnion<TRegistry>, { typeName: keyof TRegistry }>[] = [],
  ): number {
    return this.getWorld().createEntity(components);
  }

  /**
   * Mark an entity for destruction. Adds MarkedForDestruction component.
   */
  markEntityForDestruction(entityId: number): void {
    const world = this.getWorld();
    // Type assertion needed because TypeScript cannot fully resolve the generic
    // intersection between CreateComponentArgs and the component registry here.
    const data = {} as CreateComponentArgs<
      TRegistry,
      GetDefaults<TRegistry>,
      'MarkedForDestruction'
    >;

    const component = world.createComponent('MarkedForDestruction', data);
    this.addComponent(entityId, component);
  }

  /**
   * Destroy an entity. The World auto-generates an EntityDestroyedPayload.
   */
  destroyEntity(entityId: number): void {
    this.getWorld().destroyEntity(entityId);
  }

  /**
   * Build a snapshot of an entity for sending to clients (e.g., on AOI entry).
   */
  forceEntitySnapshot(entityId: number): EntityStatePayload {
    return buildEntitySnapshot(this.getWorld(), entityId);
  }
}

/**
 * Arguments passed to updateEntity in filtered component systems.
 */
export interface FilteredComponentSystemUpdateEntityArgs<
  TRegistry extends RegistryConstraint,
  TWorld extends World<TRegistry> = World<TRegistry>,
> {
  world: TWorld;
  entityId: number;
  deltaTime: number;
}

/**
 * Base class for systems that process entities matching a component query.
 */
export abstract class FilteredComponentSystem<
  TWorld extends World<TRegistry>,
  TRegistry extends CommonRegistry,
> extends BaseSystem<TWorld, TRegistry> {
  public componentClasses: (keyof TRegistry)[] = [];
  public excludedComponentClasses: (keyof TRegistry)[] = [];

  /**
   * If true, only process entities within SIMULATION_RADIUS of any player.
   * Dormant entities outside all player zones will be skipped.
   */
  protected simulationBound: boolean = false;

  /**
   * If true, this system will skip entities that have the 'Dead' component.
   * Default is false to allow processing of dead entities (e.g. RenderSystem).
   */
  protected skipDeadEntities: boolean = false;

  /**
   * Probability (0-1) that each entity is processed.
   * 1 = process all (default), 0.1 = 10% chance per entity.
   */
  protected entityProbability: number = 1;

  /**
   * Check if a specific entity should be processed based on entity probability.
   * Override for custom per-entity logic.
   * @param entityId The entity ID (available for subclass overrides)
   */
  protected shouldProcessEntity(entityId: number): boolean {
    void entityId;
    return this.entityProbability >= 1 || Math.random() < this.entityProbability;
  }

  /**
   * If true, only process the local player entity (client-side only).
   * Override getLocalPlayerEntityId() to provide the entity ID.
   */
  protected localPlayerOnly: boolean = false;

  /**
   * Components this system requires but doesn't query for.
   * These are auto-created if missing before updateEntity is called.
   * Maps component name to an initializer function that returns the component data.
   * For components with defaults, the initializer can return partial data.
   *
   * Example:
   * ```typescript
   * requiredComponents = {
   *   VisualPosition: (world, entityId) => {
   *     const t = world.getComponent(entityId, 'GlobalTransform')!;
   *     return { x: t.x, y: t.y };
   *   },
   * };
   * ```
   */
  protected requiredComponents: {
    [K in keyof TRegistry]?: (
      world: TWorld,
      entityId: number,
    ) => CreateComponentArgs<TRegistry, GetDefaults<TRegistry>, K>;
  } = {};

  /**
   * Override in client systems to return the local player's entity ID.
   */
  protected getLocalPlayerEntityId(): number | undefined {
    return undefined;
  }

  run(deltaTime: number = 1): void {
    if (!this.shouldRun()) {
      return;
    }

    const world = this.getWorld();
    let entities = world.query(this.componentClasses, this.excludedComponentClasses);

    // Filter to only active entities if this system is simulation-bound
    // Skip if SimulationService has no active entities (e.g., client-side)
    if (this.simulationBound) {
      if (this.services.simulation.getActiveCount() > 0) {
        entities = entities.filter((id: number) => this.services.simulation.isActive(id));
      }
    }

    // Filter out Dead entities if skipDeadEntities is true
    if (this.skipDeadEntities) {
      entities = entities.filter((id: number) => !world.hasComponent(id, 'Dead'));
    }

    // Filter to only local player if this system is local-player-only
    if (this.localPlayerOnly) {
      const localEntityId = this.getLocalPlayerEntityId();
      if (localEntityId !== undefined) {
        entities = entities.filter((id: number) => id === localEntityId);
      } else {
        entities = [];
      }
    }

    for (const entityId of entities) {
      if (!this.shouldProcessEntity(entityId)) {
        continue;
      }
      // Ensure required components exist before processing
      this.ensureRequiredComponents(world, entityId);
      this.updateEntity({ world, entityId, deltaTime });
    }
  }

  /**
   * Ensures all required components exist on an entity before processing.
   * Creates missing components using their initializer functions.
   */
  private ensureRequiredComponents(world: TWorld, entityId: number): void {
    for (const componentName in this.requiredComponents) {
      const key = componentName as keyof TRegistry;
      if (!world.hasComponent(entityId, key)) {
        const initializer = this.requiredComponents[key];
        if (initializer) {
          const data = initializer(world, entityId);
          const component = world.createComponent(key, data);
          world.addComponent(entityId, component);
        }
      }
    }
  }

  /**
   * Process a single entity. ECS mutations auto-generate payloads.
   */
  abstract updateEntity({
    world,
    entityId,
    deltaTime,
  }: FilteredComponentSystemUpdateEntityArgs<TRegistry, TWorld>): void;
}

/**
 * Base class for systems that process events from the event bus.
 * Unlike global or filtered systems which run every tick on entities,
 * EventSystem reacts ONLY to specific ECS events fired in the EventBus.
 *
 * This reduces boilerplate for audio, UI triggers, or cross-system notifications.
 */
export abstract class EventSystem<
  TWorld extends World<TRegistry>,
  TRegistry extends CommonRegistry,
  TEvent extends keyof EventNameToType = keyof EventNameToType,
> extends BaseSystem<TWorld, TRegistry> {
  /**
   * List of event names this system listens to.
   */
  public abstract eventTypes: TEvent[];

  run(_deltaTime: number): void {
    if (!this.shouldRun()) {
      return;
    }

    void _deltaTime;
    const eventBus = this.getEventBus();

    for (const eventType of this.eventTypes) {
      const events = eventBus.getEventsOfType(eventType);
      for (const event of events) {
        this.handleEvent(event);
      }
    }
  }

  /**
   * Handle a single event. ECS mutations auto-generate payloads.
   * @param event The event to handle
   */
  abstract handleEvent(event: EventNameToType[TEvent]): void;
}

/**
 * Base class for common global systems (no component filtering).
 */
export abstract class CommonGlobalSystem extends BaseSystem<
  World<CommonRegistry>,
  CommonRegistry
> {}

/**
 * Base class for common filtered component systems.
 */
export abstract class CommonFilteredComponentSystem extends FilteredComponentSystem<
  World<CommonRegistry>,
  CommonRegistry
> {}

/**
 * Base class for common event systems.
 * Provides access to common world and registry.
 */
export abstract class CommonEventSystem<
  TEvent extends keyof EventNameToType = keyof EventNameToType,
> extends EventSystem<World<CommonRegistry>, CommonRegistry, TEvent> {}

/**
 * Arguments passed to updatePlayer in player update systems.
 */
export interface PlayerUpdateSystemArgs<
  TRegistry extends CommonRegistry,
  TWorld extends World<TRegistry> = World<TRegistry>,
> {
  playerId: number;
  entityId: number;
  deltaTime: number;
  world: TWorld;
}

/**
 * Base class for systems that iterate through all connected players.
 * Subclasses must implement getPlayerIds() and getEntityIdForPlayer() to
 * provide access to player data from their respective services.
 */
export abstract class PlayerUpdateSystem<
  TWorld extends World<TRegistry>,
  TRegistry extends CommonRegistry,
> extends BaseSystem<TWorld, TRegistry> {
  run(deltaTime: number): void {
    const world = this.getWorld();

    for (const playerId of this.getPlayerIds()) {
      const entityId = this.getEntityIdForPlayer(playerId);
      if (entityId === undefined) continue;
      this.updatePlayer({ playerId, entityId, deltaTime, world });
    }
  }

  /**
   * Get all player IDs to iterate over.
   * Override in server/client to use appropriate service.
   */
  protected abstract getPlayerIds(): number[];

  /**
   * Get the entity ID for a player.
   * Override in server/client to use appropriate service.
   */
  protected abstract getEntityIdForPlayer(playerId: number): number | undefined;

  /**
   * Process a single player. ECS mutations auto-generate payloads.
   */
  abstract updatePlayer(args: PlayerUpdateSystemArgs<TRegistry, TWorld>): void;
}

/**
 * Base class for common player update systems.
 */
export abstract class CommonPlayerUpdateSystem extends PlayerUpdateSystem<
  World<CommonRegistry>,
  CommonRegistry
> {}
